import { useEffect, useRef, useCallback, useState } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import { useQueryClient } from '@tanstack/react-query'
import { API_URL } from '../lib/api'
import { deviceKeys } from './useDeviceQuery'

type SSEEventHandler = (event: string, data: unknown) => void

interface SSEOptions {
  enabled?: boolean
  onConnected?: () => void
  onDisconnected?: () => void
  onError?: (error: Error) => void
}

interface SSEConnection {
  eventSource: EventSource
  handlers: Map<string, Set<SSEEventHandler>>
}

interface SseTokenResponse {
  token: string
  expiresIn: number
}

let globalConnection: SSEConnection | null = null
let globalConnectionPromise: Promise<SSEConnection | null> | null = null
let connectionRefCount = 0
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 10
const INITIAL_RECONNECT_DELAY_MS = 2000
const MAX_RECONNECT_DELAY_MS = 60000
let isReconnecting = false

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && connectionRefCount > 0) {
      reconnectAttempts = 0
      if (!globalConnection || globalConnection.eventSource.readyState === EventSource.CLOSED) {
        if (!globalConnectionPromise && !isReconnecting) {
          globalConnectionPromise = createSSEConnection().then((conn) => {
            globalConnection = conn
            globalConnectionPromise = null
            return conn
          })
        }
      }
    }
  })
}

async function fetchSseToken(): Promise<SseTokenResponse | null> {
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/sse-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    })
    if (response.status === 429 || !response.ok) return null
    return (await response.json()) as SseTokenResponse
  } catch {
    return null
  }
}

async function createSSEConnection(): Promise<SSEConnection | null> {
  const tokenResponse = await fetchSseToken()
  if (!tokenResponse?.token) return null

  const url = `${API_URL}/api/v1/dashboard/events?token=${encodeURIComponent(tokenResponse.token)}`
  const eventSource = new EventSource(url, { withCredentials: true })

  const connection: SSEConnection = { eventSource, handlers: new Map() }

  eventSource.onopen = () => {
    reconnectAttempts = 0
  }

  eventSource.onerror = () => {
    if (eventSource.readyState === EventSource.CLOSED) {
      if (connectionRefCount > 0 && !isReconnecting) {
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          isReconnecting = true
          reconnectAttempts++
          const delay = Math.min(INITIAL_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts - 1), MAX_RECONNECT_DELAY_MS)
          setTimeout(() => {
            isReconnecting = false
            if (connectionRefCount > 0) {
              globalConnectionPromise = createSSEConnection().then((conn) => {
                globalConnection = conn
                globalConnectionPromise = null
                return conn
              })
            }
          }, delay)
        } else {
          reconnectAttempts = Math.floor(MAX_RECONNECT_ATTEMPTS / 2)
        }
      }
    }
  }

  eventSource.addEventListener('connected', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('connected', data)
  })

  eventSource.addEventListener('heartbeat', () => {})

  eventSource.addEventListener('command_status', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('command_status', data)
  })

  eventSource.addEventListener('milestone', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('milestone', data)
  })

  eventSource.addEventListener('device_update', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('device_update', data)
  })

  eventSource.addEventListener('device_removed', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('device_removed', data)
  })

  eventSource.addEventListener('device_registered', (event) => {
    const data = JSON.parse((event as MessageEvent).data)
    notifyHandlers('device_registered', data)
  })

  return connection
}

function notifyHandlers(event: string, data: unknown): void {
  if (!globalConnection) return
  const handlers = globalConnection.handlers.get(event)
  if (handlers) {
    handlers.forEach((handler) => {
      try {
        handler(event, data)
      } catch (err) {
        Sentry.captureException(err)
      }
    })
  }
}

function addHandler(event: string, handler: SSEEventHandler): void {
  if (!globalConnection) return
  if (!globalConnection.handlers.has(event)) {
    globalConnection.handlers.set(event, new Set())
  }
  globalConnection.handlers.get(event)!.add(handler)
}

function removeHandler(event: string, handler: SSEEventHandler): void {
  if (!globalConnection) return
  const handlers = globalConnection.handlers.get(event)
  if (handlers) {
    handlers.delete(handler)
    if (handlers.size === 0) globalConnection.handlers.delete(event)
  }
}

export function forceSSEReconnect(): void {
  if (connectionRefCount === 0) return
  reconnectAttempts = 0
  isReconnecting = false
  if (globalConnection) {
    globalConnection.eventSource.close()
    globalConnection = null
  }
  if (!globalConnectionPromise) {
    globalConnectionPromise = createSSEConnection().then((conn) => {
      globalConnection = conn
      globalConnectionPromise = null
      return conn
    })
  }
}

export function useSSE(options: SSEOptions = {}) {
  const { enabled = true, onConnected, onDisconnected, onError } = options
  const [isConnected, setIsConnected] = useState(false)
  const handlersRef = useRef<Map<string, SSEEventHandler>>(new Map())

  // Store callbacks in refs so they don't cause effect re-runs
  const onConnectedRef = useRef(onConnected)
  const onDisconnectedRef = useRef(onDisconnected)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onConnectedRef.current = onConnected
    onDisconnectedRef.current = onDisconnected
    onErrorRef.current = onError
  })

  useEffect(() => {
    if (!enabled) return
    connectionRefCount++
    if (!globalConnection && !globalConnectionPromise) {
      globalConnectionPromise = createSSEConnection().then((conn) => {
        globalConnection = conn
        globalConnectionPromise = null
        return conn
      })
    }
    const connectedHandler: SSEEventHandler = () => {
      setIsConnected(true)
      onConnectedRef.current?.()
    }
    addHandler('connected', connectedHandler)
    handlersRef.current.set('connected', connectedHandler)
    if (globalConnection?.eventSource.readyState === EventSource.OPEN) {
      setIsConnected(true)
    }
    return () => {
      connectionRefCount--
      const currentHandlers = handlersRef.current
      currentHandlers.forEach((handler, event) => removeHandler(event, handler))
      currentHandlers.clear()
      if (connectionRefCount === 0 && globalConnection) {
        globalConnection.eventSource.close()
        globalConnection = null
        setIsConnected(false)
        onDisconnectedRef.current?.()
      }
    }
  }, [enabled])

  const subscribe = useCallback((event: string, handler: SSEEventHandler) => {
    addHandler(event, handler)
    handlersRef.current.set(event, handler)
    return () => {
      removeHandler(event, handler)
      handlersRef.current.delete(event)
    }
  }, [])

  return { isConnected, subscribe }
}

export function useCommandSSE(
  commandId: string,
  options: { enabled?: boolean; onStatusUpdate?: (status: string) => void; onMilestoneUpdate?: (milestone: unknown) => void } = {},
) {
  const { enabled = true, onStatusUpdate, onMilestoneUpdate } = options
  const queryClient = useQueryClient()
  const { isConnected, subscribe } = useSSE({ enabled })

  useEffect(() => {
    if (!enabled || !commandId) return
    const unsubscribeStatus = subscribe('command_status', (_, data: unknown) => {
      const statusData = data as { id: string; status: string }
      if (statusData.id === commandId) {
        queryClient.invalidateQueries({ queryKey: ['commands', commandId] })
        onStatusUpdate?.(statusData.status)
      }
    })
    const unsubscribeMilestone = subscribe('milestone', (_, data: unknown) => {
      const milestoneData = data as { commandId: string }
      if (milestoneData.commandId === commandId) {
        queryClient.invalidateQueries({ queryKey: ['commands', commandId, 'milestones'] })
        onMilestoneUpdate?.(milestoneData)
      }
    })
    return () => {
      unsubscribeStatus()
      unsubscribeMilestone()
    }
  }, [enabled, commandId, subscribe, queryClient, onStatusUpdate, onMilestoneUpdate])

  return { isConnected }
}

export function useDeviceSSE(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const queryClient = useQueryClient()
  const { isConnected, subscribe } = useSSE({ enabled })

  useEffect(() => {
    if (!enabled) return

    const unsubs = [
      subscribe('device_update', () => {
        queryClient.invalidateQueries({ queryKey: deviceKeys.all })
        queryClient.invalidateQueries({ queryKey: deviceKeys.detected() })
      }),
      subscribe('device_removed', () => {
        queryClient.invalidateQueries({ queryKey: deviceKeys.all })
      }),
      subscribe('device_registered', () => {
        queryClient.invalidateQueries({ queryKey: deviceKeys.all })
        queryClient.invalidateQueries({ queryKey: deviceKeys.detected() })
      }),
    ]
    return () => unsubs.forEach((unsub) => unsub())
  }, [enabled, subscribe, queryClient])

  return { isConnected }
}
